import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Bot,
  FileText,
  Download,
  Library,
  FolderKanban,
  Archive,
  ShieldCheck,
  Sun,
  Moon,
  LogOut,
  PanelLeftClose,
  PanelLeft,
  Menu,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { clearCredentials } from '@/store/authSlice';
import { authApi } from '@/queries/auth.queries';
import { useTheme } from '@/hooks';
import { cx, initials } from '@/lib/utils';

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  description: string;
  soon?: boolean;
  perm?: string;
};

const NAV: readonly NavItem[] = [
  { to: '/foxit', label: 'FoxIT', icon: Bot, description: 'Ask about Saint Fox policies and the knowledge base' },
  { to: '/standardizer', label: 'Standardizer', icon: FileText, description: 'Clean a draft to the Saint Fox standard' },
  { to: '/downloads', label: 'Downloads', icon: Download, description: 'View and manage your generated documents' },
  { to: '/library', label: 'Library', icon: Library, perm: 'library.view', description: 'Browse and download company policies' },
  { to: '/projects', label: 'Projects', icon: FolderKanban, soon: true, description: 'Coming soon' },
  { to: '/vault', label: 'Vault', icon: Archive, soon: true, description: 'Coming soon' },
  { to: '/admin', label: 'Admin', icon: ShieldCheck, perm: 'audit.view', description: 'Governance and observability' },
];

const SIDEBAR_KEY = 'sf-sidebar-collapsed';

export function Layout() {
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const { theme, toggle } = useTheme();
  const { pathname } = useLocation();

  const brandLogoSrc =
    theme === 'dark' ? '/images/white-stfox-logo.png' : '/images/black-stfox-logo.png';
  const faviconSrc =
    theme === 'dark' ? '/images/white-favicon.png' : '/images/black-favicon.png';

  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Restore + persist the collapsed state.
  useEffect(() => {
    if (localStorage.getItem(SIDEBAR_KEY) === 'true') setCollapsed(true);
  }, []);
  useEffect(() => {
    localStorage.setItem(SIDEBAR_KEY, String(collapsed));
  }, [collapsed]);

  // Close the user menu on outside click.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    if (menuOpen) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  // Close the mobile drawer on route change.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  // Lock scroll + Escape while the mobile drawer is open.
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMobileNavOpen(false);
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  if (!user) return null;

  const visibleNav = NAV.filter((n) => !n.perm || user.permissions.includes(n.perm));
  const userInitials = initials(user.displayName, user.email);
  const currentItem = visibleNav.find((n) => pathname.startsWith(n.to)) ?? visibleNav[0];
  const CurrentIcon = currentItem.icon;

  async function logout() {
    // End the local session only: clear the token and return to the login
    // screen. We deliberately do not federate to Microsoft's logout endpoint,
    // so the user's Entra (portal) session stays signed in.
    await authApi.logout().catch(() => undefined);
    dispatch(clearCredentials());
    window.location.href = '/';
  }

  const renderNav = (onNavigate?: () => void) =>
    visibleNav.map((item) => {
      const Icon = item.icon;
      return (
        <NavLink
          key={item.to}
          to={item.to}
          title={collapsed ? item.label : undefined}
          onClick={onNavigate}
          className={({ isActive }) =>
            cx(
              'group relative flex items-center rounded-md text-sm transition-colors',
              collapsed ? 'justify-center h-10 w-10 mx-auto' : 'gap-3 px-3 py-2',
              isActive
                ? 'bg-primary text-primary-foreground font-medium'
                : 'text-sidebar-foreground hover:bg-accent hover:text-foreground',
            )
          }
        >
          {({ isActive }) => (
            <>
              <Icon
                className={cx(
                  'h-4 w-4 shrink-0 transition-colors',
                  isActive
                    ? 'text-primary-foreground'
                    : 'text-muted-foreground group-hover:text-foreground',
                )}
                strokeWidth={isActive ? 2.25 : 2}
              />
              {!collapsed && <span className="flex-1">{item.label}</span>}
              {!collapsed && item.soon && (
                <span
                  className={cx(
                    'ml-auto text-[10px] font-mono rounded px-1.5 py-0.5 border',
                    isActive
                      ? 'border-primary-foreground/30 text-primary-foreground/80'
                      : 'border-border text-muted-foreground',
                  )}
                >
                  soon
                </span>
              )}
            </>
          )}
        </NavLink>
      );
    });

  return (
    <div className="h-dvh flex bg-background text-foreground overflow-hidden">
      {/* Desktop sidebar */}
      <aside
        className={cx(
          'hidden md:flex shrink-0 flex-col h-full border-r border-sidebar-border bg-sidebar transition-[width] duration-200 ease-out',
          collapsed ? 'w-16' : 'w-64',
        )}
      >
        {/* Brand + collapse toggle */}
        <div className={cx('flex items-center shrink-0', collapsed ? 'justify-center py-4' : 'px-4 py-3')}>
          <NavLink to="/foxit" className={cx('flex items-center min-w-0', collapsed && 'justify-center')}>
            {collapsed ? (
              <img src={faviconSrc} alt="Saint Fox" className="h-9 w-9 object-contain shrink-0" />
            ) : (
              <img
                src={brandLogoSrc}
                alt="Saint Fox"
                className="h-9 w-auto max-w-[150px] object-contain object-left shrink-0"
              />
            )}
          </NavLink>
          {!collapsed && (
            <button
              onClick={() => setCollapsed(true)}
              aria-label="Collapse sidebar"
              title="Collapse sidebar"
              className="ml-auto h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          )}
        </div>

        {collapsed && (
          <div className="px-3 pb-2 shrink-0">
            <button
              onClick={() => setCollapsed(false)}
              aria-label="Expand sidebar"
              title="Expand sidebar"
              className="w-full h-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            >
              <PanelLeft className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="px-4 shrink-0">
          <div className="h-px bg-sidebar-border" />
        </div>

        {/* Nav */}
        <div className="flex-1 overflow-y-auto py-2">
          {!collapsed && (
            <div className="px-5 pt-3 pb-2">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Workspace
              </span>
            </div>
          )}
          <nav className="px-3 flex flex-col gap-0.5">{renderNav()}</nav>
        </div>

        {/* Footer */}
        <div
          className={cx(
            'shrink-0 border-t border-sidebar-border text-[11px] text-muted-foreground',
            collapsed ? 'flex justify-center py-3' : 'px-5 py-4 flex items-center justify-between',
          )}
        >
          {collapsed ? (
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" title="All systems normal" />
          ) : (
            <>
              <span>v1.0.0</span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Online
              </span>
            </>
          )}
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileNavOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileNavOpen(false)} />
          <aside className="relative flex flex-col h-full w-72 max-w-[85vw] bg-sidebar border-r border-sidebar-border shadow-lg">
            <div className="flex items-center justify-between px-4 py-3 shrink-0">
              <NavLink to="/foxit" className="flex items-center min-w-0">
                <img src={brandLogoSrc} alt="Saint Fox" className="h-8 w-auto max-w-[150px] object-contain object-left" />
              </NavLink>
              <button
                onClick={() => setMobileNavOpen(false)}
                aria-label="Close menu"
                className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-4 shrink-0">
              <div className="h-px bg-sidebar-border" />
            </div>
            <div className="flex-1 overflow-y-auto py-2">
              <div className="px-5 pt-3 pb-2">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  Workspace
                </span>
              </div>
              <nav className="px-3 flex flex-col gap-0.5">{renderNav(() => setMobileNavOpen(false))}</nav>
            </div>
            <div className="shrink-0 border-t border-sidebar-border px-5 py-4 text-[11px] text-muted-foreground">
              v1.0.0
            </div>
          </aside>
        </div>
      )}

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0 h-full">
        <header className="relative z-20 h-16 shrink-0 border-b border-border bg-background/90 backdrop-blur-md flex items-center gap-2 sm:gap-4 px-4 md:px-8">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open menu"
            className="md:hidden h-9 w-9 rounded-md border border-border hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0"
          >
            <Menu className="h-4 w-4" />
          </button>

          {/* Mobile brand */}
          <NavLink to="/foxit" className="md:hidden flex items-center min-w-0" aria-label="Home">
            <img src={brandLogoSrc} alt="Saint Fox" className="h-7 w-auto max-w-28 object-contain object-left" />
          </NavLink>

          {/* Desktop page title */}
          <div className="hidden md:flex items-center gap-3 min-w-0">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
              <CurrentIcon className="h-[18px] w-[18px]" />
            </span>
            <div className="min-w-0 leading-tight">
              <div className="text-sm font-semibold text-foreground truncate">{currentItem.label}</div>
              <div className="text-xs text-muted-foreground truncate">{currentItem.description}</div>
            </div>
          </div>

          <div className="ml-auto flex items-center gap-1.5">
            <button
              onClick={toggle}
              className="h-9 w-9 rounded-md border border-border hover:bg-accent flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Toggle theme"
              title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>

            <div className="mx-1 h-6 w-px bg-border" />

            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 rounded-md pl-2 pr-1 py-1 hover:bg-accent transition-colors"
              >
                <div className="hidden sm:flex flex-col items-end leading-tight">
                  <span className="text-sm font-medium">{user.displayName || 'User'}</span>
                  <span className="text-[11px] text-muted-foreground max-w-[160px] truncate">{user.email}</span>
                </div>
                <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                  {userInitials}
                </div>
              </button>

              {menuOpen && (
                <div className="absolute right-0 mt-2 w-64 rounded-lg border border-border bg-card shadow-lg z-50 overflow-hidden">
                  <div className="px-4 py-3.5 border-b border-border flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                      {userInitials}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{user.displayName}</div>
                      <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                    </div>
                  </div>
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        logout();
                      }}
                      className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-destructive hover:bg-accent text-left"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main content area. The wrapper is at least full-height and a flex
            column, so a page can use `flex-1` to fill the viewport (e.g. the
            chat); normal pages just flow and scroll. */}
        <main className="flex-1 min-h-0 overflow-y-auto">
          <div
            key={pathname}
            className="animate-rise mx-auto flex min-h-full w-full max-w-[1080px] flex-col px-4 md:px-8 py-4 sm:py-6 md:py-8"
          >
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
